import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminUsers } from '../api/client';
import type { UserProfile, AdminCreateUserBody } from '../api/client';
import BackToDashboard from '../components/BackToDashboard';
import { useAuth } from '../context/AuthContext';

const PAGE_SIZE = 25;

export default function AdminUsers() {
  const { profile } = useAuth();
  const [items, setItems] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [updatingSub, setUpdatingSub] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<AdminCreateUserBody & { confirmPassword: string }>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'customer',
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [resetSub, setResetSub] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [deleteConfirmSub, setDeleteConfirmSub] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>(''); // '' | 'active' | 'disabled'

  const filteredUsers = useMemo(() => {
    let list = [...items];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((u) => (u.email ?? '').toLowerCase().includes(q) || (u.name ?? '').toLowerCase().includes(q));
    if (filterRole) list = list.filter((u) => u.role === filterRole);
    if (filterStatus === 'active') list = list.filter((u) => u.enabled !== false);
    if (filterStatus === 'disabled') list = list.filter((u) => u.enabled === false);
    list.sort((a, b) => (a.email ?? '').localeCompare(b.email ?? ''));
    return list;
  }, [items, search, filterRole, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const effectivePage = Math.min(page, totalPages);
  const paginatedItems = useMemo(() => filteredUsers.slice((effectivePage - 1) * PAGE_SIZE, effectivePage * PAGE_SIZE), [filteredUsers, effectivePage]);

  useEffect(() => {
    setPage(1);
  }, [search, filterRole, filterStatus]);

  const refresh = () => {
    setLoading(true);
    setError(null);
    setPage(1);
    adminUsers
      .list()
      .then((res) => setItems(res.items))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const changeRole = async (sub: string, role: 'customer' | 'admin') => {
    setUpdatingSub(sub);
    setError(null);
    try {
      await adminUsers.updateRole(sub, role);
      setItems((prev) => prev.map((u) => (u.sub === sub ? { ...u, role } : u)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUpdatingSub(null);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createForm.password !== createForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (createForm.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setCreateSubmitting(true);
    setError(null);
    try {
      const created = await adminUsers.create({
        email: createForm.email.trim(),
        password: createForm.password,
        name: (createForm.name ?? '').trim() || undefined,
        role: createForm.role,
      });
      setItems((prev) => [created, ...prev]);
      setShowCreate(false);
      setCreateForm({ email: '', password: '', confirmPassword: '', name: '', role: 'customer' });
      setCreateSuccess(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleDelete = async (u: UserProfile) => {
    setError(null);
    try {
      await adminUsers.delete(u.sub, u.cognitoOnly ? { email: u.email } : undefined);
      setItems((prev) => prev.filter((x) => x.sub !== u.sub));
      setDeleteConfirmSub(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDisable = async (sub: string) => {
    setUpdatingSub(sub);
    setError(null);
    try {
      await adminUsers.disable(sub);
      setItems((prev) => prev.map((u) => (u.sub === sub ? { ...u, enabled: false } : u)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUpdatingSub(null);
    }
  };

  const handleEnable = async (sub: string) => {
    setUpdatingSub(sub);
    setError(null);
    try {
      await adminUsers.enable(sub);
      setItems((prev) => prev.map((u) => (u.sub === sub ? { ...u, enabled: true } : u)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUpdatingSub(null);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetSub) return;
    if (resetPassword !== resetConfirm) {
      setError('Passwords do not match');
      return;
    }
    if (resetPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setResetSubmitting(true);
    setError(null);
    try {
      await adminUsers.resetPassword(resetSub, resetPassword);
      setResetSub(null);
      setResetPassword('');
      setResetConfirm('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResetSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dash-page dash-page--wide">
        <BackToDashboard />
        <p style={{ color: '#6b7280' }}>Loading users…</p>
      </div>
    );
  }

  return (
    <div className="dash-page dash-page--wide">
      <BackToDashboard />
      <header className="dash-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="dash-page-title">User management</h1>
          <p className="dash-page-desc">
            Create users, change roles, revoke or re-enable access, reset passwords, and delete users. All Cognito users are listed (including unverified).
          </p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} className="dash-btn dash-btn--primary">
          + Create user
        </button>
      </header>

      {createSuccess && (
        <div className="dash-alert dash-alert--success">
          <p style={{ margin: 0, fontWeight: 700 }}>Account created</p>
          <p style={{ margin: '0.5rem 0 1rem', fontSize: '0.9375rem' }}>The user account has been created and is ready to use.</p>
          <div className="dash-actions-inline">
            <Link to="/dashboard" className="dash-btn dash-btn--primary" style={{ textDecoration: 'none' }}>Go to dashboard</Link>
            <button type="button" onClick={() => setCreateSuccess(false)} className="dash-btn dash-btn--secondary">Dismiss</button>
          </div>
        </div>
      )}

      {error && (
        <div className="dash-alert dash-alert--error">{error}</div>
      )}

      {showCreate && (
        <div className="dash-modal-overlay" onClick={() => !createSubmitting && setShowCreate(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h2 className="dash-modal-title">Create user</h2>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="dash-modal-body">
                <div className="dash-form-group">
                  <label className="dash-form-label">Email (used as username)</label>
                  <input type="email" required className="dash-form-input" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} placeholder="user@example.com" />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">Display name (optional)</label>
                  <input type="text" className="dash-form-input" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} placeholder="John Doe" />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">Password (min 8 chars, upper, lower, number)</label>
                  <input type="password" required minLength={8} className="dash-form-input" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} autoComplete="new-password" />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">Confirm password</label>
                  <input type="password" required className="dash-form-input" value={createForm.confirmPassword} onChange={(e) => setCreateForm((f) => ({ ...f, confirmPassword: e.target.value }))} autoComplete="new-password" />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">Role</label>
                  <select className="dash-form-select" value={createForm.role} onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value as 'customer' | 'admin' }))}>
                    <option value="customer">Customer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="dash-modal-footer">
                <button type="submit" disabled={createSubmitting} className="dash-btn dash-btn--primary">{createSubmitting ? 'Creating…' : 'Create user'}</button>
                <button type="button" disabled={createSubmitting} onClick={() => setShowCreate(false)} className="dash-btn dash-btn--secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetSub && (
        <div className="dash-modal-overlay" onClick={() => !resetSubmitting && setResetSub(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h2 className="dash-modal-title">Reset password</h2>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="dash-modal-body">
                <div className="dash-form-group">
                  <label className="dash-form-label">New password (min 8 chars)</label>
                  <input type="password" required minLength={8} className="dash-form-input" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} autoComplete="new-password" />
                </div>
                <div className="dash-form-group">
                  <label className="dash-form-label">Confirm new password</label>
                  <input type="password" required className="dash-form-input" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} autoComplete="new-password" />
                </div>
              </div>
              <div className="dash-modal-footer">
                <button type="submit" disabled={resetSubmitting} className="dash-btn dash-btn--primary">{resetSubmitting ? 'Resetting…' : 'Reset password'}</button>
                <button type="button" disabled={resetSubmitting} onClick={() => setResetSub(null)} className="dash-btn dash-btn--secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="dash-card">
          <div className="dash-card-body">
            <div className="dash-empty">No users yet. Create one to get started.</div>
          </div>
        </div>
      ) : (
        <section className="dash-card">
          <div className="dash-card-body">
            <div className="dash-toolbar">
              <input
                type="search"
                placeholder="Search by email or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dash-form-input"
                style={{ minWidth: 200 }}
              />
              <span className="dash-toolbar-label">Role</span>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="dash-form-select">
                <option value="">All</option>
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
              <span className="dash-toolbar-label">Status</span>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="dash-form-select">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <p className="dash-pagination-info" style={{ marginBottom: '0.75rem', textAlign: 'left' }}>
              Showing {filteredUsers.length === 0 ? 0 : ((effectivePage - 1) * PAGE_SIZE) + 1}–{Math.min(effectivePage * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} users
            </p>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Verification</th>
                    <th>Status</th>
                    <th>Org</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedItems.map((u) => (
                    <tr key={u.sub}>
                      <td>{u.email}</td>
                      <td>{u.name || '—'}</td>
                      <td>{u.phone || '—'}</td>
                      <td><span className={`dash-badge ${u.role === 'admin' ? 'dash-badge--info' : 'dash-badge--neutral'}`}>{u.role}</span></td>
                      <td>
                        {u.cognitoStatus === 'UNCONFIRMED' ? <span className="dash-badge dash-badge--warning">Not verified</span> : u.cognitoOnly ? <span className="dash-badge dash-badge--info">Never signed in</span> : u.cognitoStatus === 'CONFIRMED' ? <span className="dash-badge dash-badge--success">Verified</span> : <span className="dash-badge dash-badge--neutral">{u.cognitoStatus || '—'}</span>}
                      </td>
                      <td>{u.enabled === false ? <span className="dash-badge dash-badge--danger">Disabled</span> : <span className="dash-badge dash-badge--success">Active</span>}</td>
                      <td style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{u.orgId}</td>
                      <td>
                        <div className="dash-actions-inline">
                          {!u.cognitoOnly && (
                            <>
                              {profile?.sub === u.sub && u.role === 'admin' ? (
                                <span className="dash-badge dash-badge--info" title="Your admin role cannot be changed">Admin (you)</span>
                              ) : (
                                <select
                                  value={u.role}
                                  disabled={updatingSub === u.sub}
                                  onChange={(e) => changeRole(u.sub, e.target.value as 'customer' | 'admin')}
                                  className="dash-form-select"
                                  style={{ width: 'auto', minWidth: 100 }}
                                >
                                  <option value="customer">Customer</option>
                                  <option value="admin">Admin</option>
                                </select>
                              )}
                              {u.enabled === false ? (
                                <button type="button" disabled={updatingSub === u.sub} onClick={() => handleEnable(u.sub)} className="dash-btn dash-btn--secondary dash-btn--sm">Enable</button>
                              ) : (
                                <button type="button" disabled={updatingSub === u.sub} onClick={() => handleDisable(u.sub)} className="dash-btn dash-btn--secondary dash-btn--sm">Revoke</button>
                              )}
                              <button type="button" disabled={updatingSub === u.sub} onClick={() => setResetSub(u.sub)} className="dash-btn dash-btn--secondary dash-btn--sm">Reset password</button>
                            </>
                          )}
                          {u.cognitoOnly && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Delete only</span>}
                          {deleteConfirmSub === u.sub ? (
                            <>
                              <button type="button" onClick={() => handleDelete(u)} className="dash-btn dash-btn--danger dash-btn--sm">Confirm delete</button>
                              <button type="button" onClick={() => setDeleteConfirmSub(null)} className="dash-btn dash-btn--secondary dash-btn--sm">Cancel</button>
                            </>
                          ) : (
                            <button type="button" disabled={updatingSub === u.sub} onClick={() => setDeleteConfirmSub(u.sub)} className="dash-btn dash-btn--secondary dash-btn--sm" style={{ color: '#b91c1c' }}>Delete</button>
                          )}
                          {updatingSub === u.sub && <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Updating…</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="dash-pagination">
                <button type="button" disabled={effectivePage <= 1} onClick={() => setPage((p) => p - 1)} className="dash-btn dash-btn--secondary dash-btn--sm">Previous</button>
                <span className="dash-pagination-info">Page {effectivePage} of {totalPages}</span>
                <button type="button" disabled={effectivePage >= totalPages} onClick={() => setPage((p) => p + 1)} className="dash-btn dash-btn--secondary dash-btn--sm">Next</button>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
